import { logNetworkError } from 'calling-error-reporting/report/error';
import { STARTED, SUCCEEDED, FAILED, UNINITIALIZED } from 'calling-integration-sdk-support/async-data/constants/AsyncStatuses';
import { useAsyncData } from 'calling-integration-sdk-support/async-data/hooks/useAsyncData';
import { getProviderFromLocalStorage } from 'calling-integration-sdk-support/external-provider/operators/getProviderFromLocalStorage';
import { HUBSPOT } from 'calling-internal-common/call-provider/constants/ProviderNames';
import CallingProvider from 'calling-internal-common/call-provider/records/CallingProvider';
import { getIsTwilioBasedCallProvider } from 'calling-internal-common/utils/isThirdPartyCallingPopoverInUse';
import { useCallback, useEffect, useMemo } from 'react';
import { sendCrossTabMessage } from 'worker-connection-library/calling-cross-tab-utils/sendCrossTabMessage';
import { createBroadcastMessage } from 'calling-integration-cross-tab/messages/createBroadcastMessages';
import { UPDATE_CALL_PROVIDER } from 'calling-integration-cross-tab/types/BroadcastEvents';
import { CHANGE_CALL_PROVIDER } from 'calling-global-api/constants/EventListeners';
import { setPersistedCallProvider, getPersistedCallProvider } from 'calling-lifecycle-internal/utils/getLocalCallSettings';
import { createUserSettingsUpdateRequest, updateUserSettings } from '../http/updateUserSettings';
import { useCallingPreferencesProvider } from './useCallingPreferencesProvider';
import { getHsCalling } from '../utils/getHsCalling';
import { useThirdPartyWidgetDetailsContext } from '../context/ThirdPartyWidgetDetailsContext';
import { useDispatch } from './useDispatch';
import { updateProviderDetails, useProviderDetails } from 'worker-connection-library/hooks/useProviderDetails';
import { Metrics, SELECTED_CALL_PROVIDER_MIGRATION_FAILED } from '../utils/Metrics';
import { useConnectionStatus } from 'worker-connection-library/hooks/useConnectionStatus';
import { isConnectionErrored, isConnectionLoading } from 'calling-ui-library/utils/connectionStatusHelpers';
export const initialState = {
  data: {
    name: HUBSPOT,
    usesCallingWindow: true
  },
  status: UNINITIALIZED,
  updateAsyncData: () => {},
  isThirdPartyProviderSelected: false,
  setCallingPreferencesProvider: () => Promise.resolve()
};
export const useSelectedCallProvider = ({
  setIsUserPreferencesProviderUnset
}) => {
  const {
    data,
    status,
    updateAsyncData
  } = useAsyncData(initialState.data);
  const [, dispatch] = useThirdPartyWidgetDetailsContext();
  const {
    updateWidgetDetails
  } = useDispatch(dispatch);
  const connectionStatus = useConnectionStatus();
  const updateSelectedCallProvider = useCallback(updates => {
    const {
      data: providerData,
      status: updateStatus
    } = updates;
    updateAsyncData({
      data: providerData,
      status: updateStatus
    });
    if (updateStatus === SUCCEEDED) {
      updateWidgetDetails({
        selectedCallProviderData: providerData
      });
      void updateProviderDetails({
        selectedCallProviderData: providerData
      });
    }
  }, [updateAsyncData, updateWidgetDetails]);
  const getCallingPreferencesProvider = useCallingPreferencesProvider({
    setIsUserPreferencesProviderUnset
  });
  useEffect(() => {
    if (status !== UNINITIALIZED || isConnectionLoading(connectionStatus)) {
      return;
    }
    updateAsyncData({
      status: STARTED
    });
    if (isConnectionErrored(connectionStatus)) {
      updateAsyncData({
        status: FAILED
      });
      return;
    }
    const installedCallingProvider = getProviderFromLocalStorage();
    if (installedCallingProvider && installedCallingProvider.name) {
      updateSelectedCallProvider({
        data: installedCallingProvider,
        status: SUCCEEDED
      });
      return;
    }
    const persistedProvider = getPersistedCallProvider();
    if (persistedProvider) {
      updateSelectedCallProvider({
        data: persistedProvider,
        status: SUCCEEDED
      });
    }
    void getCallingPreferencesProvider(updateSelectedCallProvider);
  }, [getCallingPreferencesProvider, status, updateAsyncData, updateSelectedCallProvider, connectionStatus]);
  const setCallingPreferencesProvider = useCallback(async provider => {
    if (!provider || !provider['name']) return;
    updateSelectedCallProvider({
      data: provider,
      status: SUCCEEDED
    });
    const request = createUserSettingsUpdateRequest(provider);
    try {
      await updateUserSettings(request);
      setIsUserPreferencesProviderUnset(false);
      sendCrossTabMessage(createBroadcastMessage({
        type: UPDATE_CALL_PROVIDER,
        payload: provider
      }));
      setPersistedCallProvider(new CallingProvider(provider));
      const hsCalling = getHsCalling();
      hsCalling.triggerEvent({
        event: CHANGE_CALL_PROVIDER,
        data: {
          callingProvider: new CallingProvider(provider)
        }
      });
    } catch (networkError) {
      logNetworkError({
        networkError,
        error: new Error('Failed to patch calling-orchestration/v1/settings/user')
      });
    }
  }, [setIsUserPreferencesProviderUnset, updateSelectedCallProvider]);
  const isThirdPartyProviderSelected = useMemo(() => {
    return !getIsTwilioBasedCallProvider(new CallingProvider(data));
  }, [data]);
  const providerDetails = useProviderDetails();
  const selectedCallProviderDataFromSharedWorker = providerDetails === null || providerDetails === void 0 ? void 0 : providerDetails.selectedCallProviderData;
  useEffect(() => {
    if (!selectedCallProviderDataFromSharedWorker) {
      Metrics.counter(SELECTED_CALL_PROVIDER_MIGRATION_FAILED).increment();
    }
  }, [selectedCallProviderDataFromSharedWorker]);
  return {
    data: selectedCallProviderDataFromSharedWorker || data,
    status,
    updateAsyncData: updateSelectedCallProvider,
    isThirdPartyProviderSelected,
    setCallingPreferencesProvider
  };
};